import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    codeBlock,
    codeBlockCopyButton,
    codeBlockWrapper,
} from '@components/RichTextPlate/RichTextEditor.css'
import { Box, Icon } from '@ui'
import Prism from 'prismjs'

import '../../prismTheme.css'

import { loadPrismLanguage } from '../../utils/prismLanguages'

interface CodeBlockPreviewProps {
    children?: React.ReactNode
    className?: string
    node?: {
        properties?: {
            className?: string[]
            dataLanguage?: string
        }
    }
}

/**
 * Code block component for timeline preview with Prism syntax highlighting.
 * Extracts language from dataLanguage attribute (set by remarkPreserveCodeLanguage)
 * or falls back to className.
 */
export const CodeBlockPreview: React.FC<CodeBlockPreviewProps> = ({
    children,
    className,
    node,
}) => {
    // Extract language from multiple sources (in priority order):
    // 1. dataLanguage on pre element (set by remarkPreserveCodeLanguage)
    // 2. className on pre element (language-X)
    // 3. className on code child element
    const language = useMemo(() => {
        // Check pre element's dataLanguage (set by remark plugin via hProperties)
        if (node?.properties?.dataLanguage) {
            return node.properties.dataLanguage
        }

        // Check pre element className
        const preClassNames = node?.properties?.className || (className ? [className] : [])
        const preLangClass = preClassNames.find((c: string) => c.startsWith('language-'))
        if (preLangClass) {
            return preLangClass.replace('language-', '')
        }

        // Check code child className
        if (React.isValidElement(children)) {
            const childProps = children.props as { className?: string }
            const codeClassName = childProps?.className || ''
            const match = codeClassName.match(/language-(\w+)/)
            if (match) {
                return match[1]
            }
        }

        return ''
    }, [node, className, children])

    // Get the text content from children
    const code = useMemo(() => {
        if (typeof children === 'string') {
            return children
        }
        if (React.isValidElement(children)) {
            const childProps = children.props as { children?: React.ReactNode }
            if (typeof childProps?.children === 'string') {
                return childProps.children
            }
        }
        // For nested structures, extract text recursively
        const extractText = (reactNode: React.ReactNode): string => {
            if (typeof reactNode === 'string') {
                return reactNode
            }
            if (Array.isArray(reactNode)) {
                return reactNode.map(extractText).join('')
            }
            if (React.isValidElement(reactNode)) {
                const props = reactNode.props as { children?: React.ReactNode }
                return extractText(props?.children)
            }
            return ''
        }
        return extractText(children)
    }, [children])

    // Track if language is loaded
    const [languageLoaded, setLanguageLoaded] = useState(
        !language || Prism.languages[language] !== undefined,
    )

    // Lazy load the language if needed
    useEffect(() => {
        if (!language) {
            setLanguageLoaded(true)
            return
        }
        if (Prism.languages[language]) {
            setLanguageLoaded(true)
            return
        }
        setLanguageLoaded(false)
        loadPrismLanguage(language).then((loaded) => {
            setLanguageLoaded(loaded || true) // Show code even if language fails to load
        })
    }, [language])

    // Apply Prism highlighting
    const highlightedCode = useMemo(() => {
        if (!language || !languageLoaded || !Prism.languages[language]) {
            return code
        }
        try {
            return Prism.highlight(code, Prism.languages[language], language)
        } catch {
            return code
        }
    }, [code, language, languageLoaded])

    // Copy to clipboard state
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(() => {
        navigator.clipboard
            .writeText(code)
            .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            })
            .catch((error) => {
                console.error('Failed to copy code to clipboard:', error)
            })
    }, [code])

    return (
        <Box className={codeBlockWrapper}>
            <button
                type="button"
                aria-label={copied ? 'Copied' : 'Copy code'}
                className={codeBlockCopyButton}
                onClick={handleCopy}
            >
                <Icon type={copied ? 'check' : 'copy'} size="square_xs" />
            </button>
            <Box as="pre" className={codeBlock}>
                <code
                    className={language ? `language-${language}` : undefined}
                    dangerouslySetInnerHTML={
                        highlightedCode !== code ? { __html: highlightedCode } : undefined
                    }
                >
                    {highlightedCode === code ? code : undefined}
                </code>
            </Box>
        </Box>
    )
}
